/**
 * Super-admin pump usage + license-slab analysis dashboards.
 * Metrics: tanks, nozzles, employees, ledger transactions, value (avg monthly profit), suggested plan.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { fetchAdminPumpUsageAnalytics } from './adminApi';
import './AdminPumpUsageDashboard.css';

const PLAN_COLORS = {
  Starter: '#64748b',
  Growth: '#0d9488',
  Standard: '#2563eb',
  Business: '#7c3aed',
  Enterprise: '#b45309',
};

const SCOPE_OPTIONS = [
  { value: 'operational', label: 'Operational (day-close in 60d)' },
  { value: 'active_status', label: 'Status = Active' },
  { value: 'all', label: 'All pumps' },
];

function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-IN');
}

function formatFee(value) {
  const n = Number(value || 0);
  const abs = Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  return n < 0 ? `−₹${abs}` : `₹${abs}`;
}

function planBadgeClass(plan) {
  const key = String(plan || '').toLowerCase();
  return `apu-plan-badge apu-plan-badge--${key}`;
}

export default function AdminPumpUsageDashboard({ activeTab = 'usage' }) {
  const [scope, setScope] = useState('operational');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchAdminPumpUsageAnalytics(scope)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load usage analytics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [scope]);

  const filteredPumps = useMemo(() => {
    const rows = data?.pumps || [];
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (planFilter !== 'all' && row.suggested_plan !== planFilter) return false;
      if (!q) return true;
      return (
        String(row.pump_name || '').toLowerCase().includes(q)
        || String(row.owner_name || '').toLowerCase().includes(q)
        || String(row.pump_id).includes(q)
      );
    });
  }, [data, search, planFilter]);

  if (loading) {
    return <div className="apu-loading">Loading usage analytics…</div>;
  }

  if (error) {
    return <div className="admin-dash-empty"><p className="text-danger mb-0">{error}</p></div>;
  }

  if (!data) {
    return null;
  }

  const summary = data.summary || {};
  const catalog = data.plan_catalog || [];
  const distribution = data.plan_distribution || [];

  return (
    <div className="admin-pump-usage">
      <div className="admin-pump-usage__toolbar">
        <div>
          <h5 className="mb-1">
            {activeTab === 'analysis' ? 'Pump usage analysis' : 'Usage dashboard'}
          </h5>
          <p className="text-muted small mb-0">
            As of {data.as_of}. Monthly = last 30 days · Yearly = calendar YTD · Total = lifetime.
            Value = avg monthly P&amp;L profit (90d). Suggested plan = higher of nozzles vs avg monthly txns (90d).
          </p>
        </div>
        <div className="admin-pump-usage__controls">
          <select
            className="form-select form-select-sm"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            {SCOPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {activeTab === 'usage' && (
        <>
          <div className="admin-pump-usage__kpis">
            <div className="apu-kpi">
              <div className="apu-kpi__label">Pumps in scope</div>
              <div className="apu-kpi__value">{formatNumber(summary.pump_count)}</div>
              <div className="apu-kpi__meta">{formatNumber(summary.operational_pump_count)} regular (≥20 closes / 60d)</div>
            </div>
            <div className="apu-kpi">
              <div className="apu-kpi__label">Tanks / Nozzles / Staff</div>
              <div className="apu-kpi__value">
                {formatNumber(summary.total_tanks)} / {formatNumber(summary.total_nozzles)} / {formatNumber(summary.total_employees)}
              </div>
              <div className="apu-kpi__meta">Across pumps in scope</div>
            </div>
            <div className="apu-kpi">
              <div className="apu-kpi__label">Value (Σ avg mo profit)</div>
              <div className="apu-kpi__value">{formatFee(summary.total_value)}</div>
              <div className="apu-kpi__meta">P&amp;L avg / month · last 90d</div>
            </div>
            <div className="apu-kpi">
              <div className="apu-kpi__label">Monthly txns (30d)</div>
              <div className="apu-kpi__value">{formatNumber(summary.monthly_txns)}</div>
              <div className="apu-kpi__meta">From {data.period?.rolling_30d_start}</div>
            </div>
            <div className="apu-kpi apu-kpi--accent">
              <div className="apu-kpi__label">Suggested license revenue</div>
              <div className="apu-kpi__value">{formatFee(summary.suggested_monthly_revenue)}</div>
              <div className="apu-kpi__meta">/ month if slabs applied</div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-5">
              <div className="admin-panel h-100">
                <h6 className="mb-3">Plan distribution</h6>
                {(distribution.length === 0) ? (
                  <p className="text-muted mb-0">No pumps in scope.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={distribution}
                        dataKey="count"
                        nameKey="plan"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ plan, count }) => `${plan} (${count})`}
                      >
                        {distribution.map((entry) => (
                          <Cell key={entry.plan} fill={PLAN_COLORS[entry.plan] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="col-lg-7">
              <div className="admin-panel h-100">
                <h6 className="mb-3">Top pumps by monthly transactions</h6>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={(data.top_by_monthly_txns || []).slice(0, 8)} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="pump_name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatNumber(v)} />
                    <Bar dataKey="monthly_txns" name="Monthly txns" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="admin-panel mb-3">
            <h6 className="mb-2">Recommended plans</h6>
            <p className="text-muted small mb-3">
              Final fee = higher of nozzle band and monthly-transaction band. Enterprise also if lifetime rows ≥ 2,00,000.
            </p>
            <div className="table-responsive">
              <table className="table table-sm admin-pump-table mb-0">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Monthly fee</th>
                    <th>Nozzles</th>
                    <th>Monthly txns</th>
                    <th>Annual txns</th>
                    <th>In scope</th>
                    <th>Potential ₹/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {catalog.map((plan) => {
                    const dist = distribution.find((d) => d.plan === plan.plan);
                    return (
                      <tr key={plan.plan}>
                        <td><span className={planBadgeClass(plan.plan)}>{plan.plan}</span></td>
                        <td>{formatFee(plan.fee)}</td>
                        <td>{plan.nozzles}</td>
                        <td>{plan.monthly_txns}</td>
                        <td>{plan.annual_txns}</td>
                        <td>{dist ? dist.count : 0}</td>
                        <td>{formatFee(dist ? dist.revenue : 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'analysis' && (
        <>
          <div className="admin-pump-usage__kpis admin-pump-usage__kpis--compact">
            <div className="apu-kpi">
              <div className="apu-kpi__label">Analysing</div>
              <div className="apu-kpi__value">{formatNumber(filteredPumps.length)}</div>
              <div className="apu-kpi__meta">of {formatNumber(summary.pump_count)} in scope</div>
            </div>
            <div className="apu-kpi">
              <div className="apu-kpi__label">Suggested revenue (filtered)</div>
              <div className="apu-kpi__value">
                {formatFee(filteredPumps.reduce((sum, row) => sum + (row.suggested_fee || 0), 0))}
              </div>
              <div className="apu-kpi__meta">/ month</div>
            </div>
            <div className="apu-kpi">
              <div className="apu-kpi__label">Value (filtered)</div>
              <div className="apu-kpi__value">
                {formatFee(filteredPumps.reduce((sum, row) => sum + Number(row.value || 0), 0))}
              </div>
              <div className="apu-kpi__meta">Σ avg mo profit</div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <div className="admin-panel h-100">
                <h6 className="mb-3">Nozzles vs avg monthly txns</h6>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={[...filteredPumps].sort((a, b) => b.nozzles - a.nozzles).slice(0, 12)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="pump_name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatNumber(v)} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="nozzles" name="Nozzles" fill="#0d9488" />
                    <Bar yAxisId="right" dataKey="avg_monthly_txns_90d" name="Avg mo txns (90d)" fill="#7c3aed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="admin-panel h-100">
                <h6 className="mb-3">Lifetime data held (total txns)</h6>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={(data.top_by_total_txns || []).slice(0, 10)} layout="vertical" margin={{ left: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="pump_name" width={130} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => formatNumber(v)} />
                    <Bar dataKey="total_txns" name="Lifetime txns" fill="#b45309" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="admin-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 className="mb-0">Pump usage detail</h6>
          <div className="admin-pump-usage__filters">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search pump / owner / id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="form-select form-select-sm"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <option value="all">All plans</option>
              {catalog.map((p) => (
                <option key={p.plan} value={p.plan}>{p.plan}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover table-sm admin-pump-table admin-pump-usage__table mb-0">
            <thead>
              <tr>
                <th>Pump</th>
                <th className="text-end">Tanks</th>
                <th className="text-end">Nozzles</th>
                <th className="text-end">Employees</th>
                <th className="text-end">Monthly</th>
                <th className="text-end">Yearly</th>
                <th className="text-end">Total</th>
                <th className="text-end">Avg/mo (90d)</th>
                <th className="text-end">Value</th>
                <th>Suggested plan</th>
                <th className="text-end">Fee</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredPumps.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-muted text-center py-4">No pumps match this filter.</td>
                </tr>
              ) : filteredPumps.map((row) => (
                <tr key={row.pump_id}>
                  <td>
                    <div className="admin-dash-pump-name">{row.pump_name}</div>
                    <div className="admin-dash-pump-meta">
                      #{row.pump_id}
                      {row.owner_name ? ` · ${row.owner_name}` : ''}
                      {row.is_operational ? ' · operational' : ''}
                      {row.last_close ? ` · last close ${row.last_close}` : ''}
                    </div>
                  </td>
                  <td className="text-end">{formatNumber(row.tanks)}</td>
                  <td className="text-end">{formatNumber(row.nozzles)}</td>
                  <td className="text-end">{formatNumber(row.employees ?? row.active_users)}</td>
                  <td className="text-end">{formatNumber(row.monthly_txns)}</td>
                  <td className="text-end">{formatNumber(row.yearly_txns)}</td>
                  <td className="text-end">{formatNumber(row.total_txns)}</td>
                  <td className="text-end">{formatNumber(row.avg_monthly_txns_90d)}</td>
                  <td className={`text-end fw-semibold ${Number(row.value) < 0 ? 'text-danger' : ''}`}>
                    {formatFee(row.value)}
                  </td>
                  <td>
                    <span className={planBadgeClass(row.suggested_plan)}>{row.suggested_plan}</span>
                    <div className="admin-dash-pump-meta">
                      N:{row.plan_by_nozzles} · T:{row.plan_by_txns}
                    </div>
                  </td>
                  <td className="text-end fw-semibold">{formatFee(row.suggested_fee)}</td>
                  <td className="text-end">
                    <Link to={`/admin-pump/${row.pump_id}`} className="btn btn-sm btn-outline-primary">Open</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
